<div class="page-container">
  <div class="legal-content scrollable-content">
    <h1>{{ 'legal.terms.title' | translate }}</h1>
    <p class="updated">{{ 'legal.terms.lastUpdated' | translate }}</p>

    <p>
      These Terms of Service ("Terms") govern your access to and use of CheckPoint (the "Service"), provided by
      Garden Bay Software ("Garden Bay Software", "we", "us", or "our"). By creating an account or using the Service,
      you agree to these Terms on behalf of yourself and, if applicable, the organization you represent. If you don't
      agree, don't use the Service.
    </p>

    <h2>1. The Service</h2>
    <p>
      CheckPoint is an offline-first application for tracking assets, checkpoints, forms, and related records. Data
      you enter is stored locally on your device and synced in the background to servers we operate, so that it's
      available to other authorized members of your organization and backed up outside your device.
    </p>

    <h2>2. Accounts and Organizations</h2>
    <p>
      To use the Service, you need an account belonging to an organization. You're responsible for the accuracy of
      the information you provide, for keeping your login credentials confidential, and for all activity that occurs
      under your account. Notify us promptly at
      <a href="mailto:support@gardenbaysoftware.com">support&#64;gardenbaysoftware.com</a> if you suspect unauthorized
      use of your account.
    </p>
    <p>
      Organization owners and admins can invite, manage, and remove other users within their organization, and can
      assign roles that control what those users can see and do.
    </p>

    <h2>3. Subscriptions, Fees, and Billing</h2>
    <p>
      Some features of the Service require a paid subscription. Paid subscriptions are billed in advance on a
      recurring basis (e.g., monthly or annually) and renew automatically until canceled. Payment is processed by
      Stripe; we don't store your full card details ourselves. By subscribing, you authorize us to charge your
      payment method for the applicable fees, including automatic renewals.
    </p>
    <p>
      You can cancel a subscription at any time from your account settings; cancellation takes effect at the end of
      the current billing period. Except as required by law, fees already paid are non-refundable. We may change our
      fees going forward, and will give you reasonable notice before a change takes effect.
    </p>

    <h2>4. Your Data</h2>
    <p>
      As between you and us, you and your organization own the data you enter into the Service (assets, checkpoints,
      form submissions, reports, and similar content — "Your Data"). You grant us a limited license to host, store,
      sync, and process Your Data solely to provide, maintain, and improve the Service on your behalf. We don't sell
      Your Data or use it for any purpose other than operating the Service. See our
      <a routerLink="/privacy">Privacy Policy</a> for more on what we collect and how we use it.
    </p>
    <p>
      You're responsible for the accuracy and legality of Your Data, and for having the right to enter any personal
      information about others (such as employees or contractors) that you submit through the Service.
    </p>

    <h2>5. Acceptable Use</h2>
    <p>You agree not to:</p>
    <ul>
      <li>Use the Service for any unlawful purpose or in violation of applicable laws or regulations;</li>
      <li>Attempt to gain unauthorized access to another organization's data or accounts;</li>
      <li>Interfere with or disrupt the integrity or performance of the Service;</li>
      <li>Reverse engineer, decompile, or attempt to extract the source code of the Service, except as permitted by
        law;</li>
      <li>Resell or provide the Service to third parties outside your organization without our consent.</li>
    </ul>

    <h2>6. Third-Party Services</h2>
    <p>
      We use Stripe to process payments. Your use of paid features is also subject to
      <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer">Stripe's terms and privacy
        policy</a>. We aren't responsible for the acts or omissions of third-party services.
    </p>

    <h2>7. Intellectual Property</h2>
    <p>
      We and our licensors own all right, title, and interest in the Service, including its software, design, and
      branding. These Terms don't grant you any rights to our trademarks or branding, and don't transfer ownership of
      the Service to you.
    </p>

    <h2>8. Disclaimer of Warranties</h2>
    <p>
      The Service is provided "as is" and "as available," without warranties of any kind, express or implied,
      including warranties of merchantability, fitness for a particular purpose, and non-infringement, to the
      maximum extent permitted by law. We don't guarantee the Service will be uninterrupted, error-free, or secure.
    </p>

    <h2>9. Limitation of Liability</h2>
    <p>
      To the maximum extent permitted by law, Garden Bay Software will not be liable for any indirect, incidental,
      special, consequential, or punitive damages, or any loss of data, revenue, or profits, arising from your use of
      the Service. Our total liability for any claim arising out of these Terms or the Service is limited to the
      amount you paid us in the 12 months before the claim arose.
    </p>

    <h2>10. Indemnification</h2>
    <p>
      You agree to indemnify and hold Garden Bay Software harmless from any claims, damages, or expenses (including
      reasonable legal fees) arising from your use of the Service, Your Data, or your violation of these Terms.
    </p>

    <h2>11. Termination</h2>
    <p>
      You may stop using the Service at any time. We may suspend or terminate your access if we reasonably believe
      you've violated these Terms, or if required by law. Upon termination, your right to use the Service ends, but
      provisions of these Terms that by their nature should survive (such as fees owed, ownership, disclaimers,
      liability limits, and indemnification) will continue to apply.
    </p>

    <h2>12. Changes to These Terms</h2>
    <p>
      We may update these Terms from time to time. If we make material changes, we'll notify you (for example, by
      email or through the Service) before they take effect. Continued use of the Service after changes take effect
      means you accept the updated Terms.
    </p>

    <h2>13. Governing Law</h2>
    <p>
      These Terms are governed by the laws of the Province of British Columbia and the federal laws of Canada
      applicable there, without regard to conflict-of-law principles. Nothing in these Terms limits any consumer
      protection rights you may have under the mandatory laws of your place of residence.
    </p>

    <h2>14. Contact</h2>
    <p>
      Questions about these Terms? Contact us at
      <a href="mailto:support@gardenbaysoftware.com">support&#64;gardenbaysoftware.com</a>.
    </p>

    <p class="updated">
      This document was adapted from the
      <a href="https://github.com/Automattic/legalmattic" target="_blank" rel="noopener noreferrer">Automattic
        Legalmattic</a> Terms of Service template, licensed under
      <a href="https://creativecommons.org/licenses/by-sa/4.0/" target="_blank" rel="noopener noreferrer"
        >CC BY-SA 4.0</a
      >.
    </p>

    <a routerLink="/landing" class="back-link" data-testid="terms-back-link">{{
      'legal.backToHome' | translate
    }}</a>
  </div>
</div>
